import React, { Fragment } from 'react';
import {
	Button,
	Form,
	Modal,
	Dimmer,
	Loader,
	Table,
	Icon,
	Dropdown,
	TextArea,
	Checkbox
} from 'semantic-ui-react';
import { inject, observer } from 'mobx-react';
import Rx from 'stores/Rx';
import * as styles from './styles';
import { observable, IObservableArray, toJS } from 'mobx';
import type { AssignableUser } from 'stores/Account';
import MessageBox from 'components/MessageBox';
import MsgStore from 'stores/MsgStore';
import UiShared from 'stores/UiShared';
import Auth from 'stores/Auth';
import _ from 'lodash';
import statuses from 'data/statuses';
import DayPickerInput from 'components/DayPickerInput';
import * as yup from 'yup';
import TextareaAutosize from 'react-textarea-autosize';
import finalisationLabelMap from 'data/finalisationLabelMap';
import PatientHeader from 'components/Patient';

@inject(rootStore => ({
	auth: rootStore.auth,
	uiShared: rootStore.uiShared
}))
@observer
export default class ScriptUploaded extends React.Component {
	props: {
		isOpen: boolean,
		scriptId: number,
		onRequestClose: () => {},
		auth?: Auth,
		uiShared: UiShared
	};

	static defaultProps = {
		isOpen: false
	};
	uploadInput = React.createRef();

	msgStore: MsgStore = new MsgStore();

	@observable loading: boolean = false;
	@observable showButtons: boolean = false;
	@observable showResponseMsg: boolean = false;
	@observable hideRx: boolean = false;
	@observable rx: Rx = null;
	@observable statusValue: string = '';
	@observable showDeleteConfirmDlg: boolean = false;

	@observable filesToUpload: IObservableArray<File> = [];
	@observable storedFiles = [];

	componentDidUpdate = async prevProps => {
		if (this.props.scriptId && prevProps.scriptId !== this.props.scriptId) {
			this.loading = true;
			await this.loadRx();
			this.initForm();
			this.loading = false;
		}
	};

	initForm = () => {
		this.statusValue = ['approved', 'rejected', 'deferred'].includes(this.rx?.status)
			? this.rx.status
			: '';
		this.storedFiles = [];
		this.filesToUpload.clear();
		this.msgStore.clear();
		this.setStoredFiles();
	};

	setStoredFiles = () => {
		let files = [];
		const { rx } = this;
		let order, label, lastPart, lastPartNumber;
		for (let i = 0; i < 5; i++) {
			const file = rx[`fileMan${i + 1}`];
			if (file) {
				order = 0;
				label = file.substring(file.lastIndexOf('/') + 1);
				lastPart = file.substring(file.lastIndexOf('_') + 1);
				lastPart = lastPart
					.split('.')
					.slice(0, -1)
					.join('.');
				lastPartNumber = Number(lastPart);
				if (!isNaN(lastPartNumber)) {
					order = lastPartNumber;
				}
				files.push({
					fileName: file,
					label: label,
					order: order,
					deleted: false
				});
			}
		}
		files = _.orderBy(files, ['order']);
		this.storedFiles = files;
	};

	loadRx = async () => {
		this.rx = await Rx.loadById(this.props.scriptId);
		this.showButtons = true;
	};

	removeDuplicates = (myArr, prop) => {
		return myArr.filter((obj, pos, arr) => {
			return arr.map(mapObj => mapObj[prop]).indexOf(obj[prop]) === pos;
		});
	};

	renderButtons = () => {
		if (!this.showButtons) return;
		let btn = [];
		btn.push(this.renderSave());
		return btn;
	};

	renderSave = () => {
		return (
			<Button primary onClick={() => this.save()} key="save" className={styles.greenButton}>
				Save & Exit
			</Button>
		);
	};

	getDataValidationObject = () => {
		let val = {
			uploadedtoHPOS: yup
				.date()
				.label('Uploaded to HPOS')
				.typeError('Uploaded to HPOS is a required field.')
				.required('Uploaded to HPOS is a required field.'),
			hposUploadReference: yup
				.string()
				.label('HPOS Upload Reference')
				.required()
				.nullable(),
			statusValue: yup
				.string()
				.label('Status')
				.required(),
			finalisationDate: yup
				.date()
				.label(`${finalisationLabelMap[this.statusValue]} Date`)
				.typeError(`${finalisationLabelMap[this.statusValue]} Date is a required field.`)
				.required(`${finalisationLabelMap[this.statusValue]} Date is a required field.`)
		};
		if (this.statusValue === 'approved') {
			val.approvalNumber = yup
				.string()
				.label('Approval Number')
				.required();
		}
		if (this.statusValue === 'rejected') {
			val.reasonText = yup
				.string()
				.label('Rejection Reason')
				.required();
		}
		return yup.object().shape(val);
	};

	save = async () => {
		const { uiShared } = this.props;
		try {
			this.msgStore.clear();
			uiShared.showSpinner('Saving...');
			let validationData = Object.assign({}, this.rx, {
				statusValue: this.statusValue
			});
			await this.getDataValidationObject().validate(toJS(validationData), {
				abortEarly: false
			});
			if (this.rx.status === 'approved' && this.statusValue !== this.rx.status) {
				this.rx.approvalNumber = '';
			} else if (this.rx.status === 'rejected' && this.statusValue !== this.rx.status) {
				this.rx.reasonText = '';
			}
			this.rx.status = this.statusValue;
			await this.rx.update();
			await this.props.onRequestReload();
			this.onClose();
		} catch (e) {
			this.msgStore.setError(e);
		} finally {
			uiShared.hideModal();
		}
	};

	resetForm = () => {
		this.rx = new Rx();
		this.statusValue = '';
	};

	onClose = id => {
		this.resetForm();
		this.props.onRequestClose(id);
	};

	renderDelete = () => {
		return (
			<Button
				primary
				onClick={() => {
					this.showDeleteConfirmDlg = true;
				}}
				key="delete"
				className={styles.redButton}
			>
				Delete Rx
			</Button>
		);
	};

	deleteRx = async () => {
		const { uiShared } = this.props;
		try {
			this.msgStore.clear();
			uiShared.showSpinner('Deleting...');
			const rx = await Rx.loadById(this.props.scriptId);
			rx.disabled = true;
			await rx.update();
			await this.props.onRequestReload();
			this.onClose();
		} catch (e) {
			this.msgStore.setError(e);
		} finally {
			uiShared.hideModal();
		}
	};

	render() {
		const { isOpen, auth, scriptId } = this.props;
		const {
			rx,
			loading,
			showDebug,
			showResponseMsg,
			renderResponseMsg,
			renderDebug,
			onClose,
			storedFiles,
			filesToUpload,
			statusValue
		} = this;

		let headerTitle = '';
		if (scriptId && rx?.authorityNo) {
			headerTitle = `${rx?.searchPrescriber} - ${rx?.searchPatientName} - ${rx?.authorityNo}`;
		}
		const ph = <span className={styles.ph}>-</span>;

		const rxDate = rx?.rxDate?.toDate().format() || '';
		const patient = rx?.Patient;
		const isReadOnly = auth?.user?.role === 'viewer';
		const showButtons = !isReadOnly;

		const stored = storedFiles.filter(f => !f.deleted);

		return (
			rx && (
				<Fragment>
					<Modal open={isOpen} size="small" onClose={() => onClose()} closeOnDimmerClick={false}>
						<Fragment>
							<Dimmer active={loading} inverted>
								<Loader content="Loading" />
							</Dimmer>
							<Modal.Header>{headerTitle ? headerTitle : ph}</Modal.Header>
							<Modal.Content>
								<Form>
									<MessageBox msgStore={this.msgStore} />
									<div className={styles.buttonsRowSpaced}>
										<div className={styles.buttonCont}>{showButtons && this.renderDelete()}</div>
										<div>
											{showButtons && this.renderButtons()}
											<Button onClick={() => onClose()}>Close</Button>
										</div>
									</div>
									{showResponseMsg && renderResponseMsg()}
									{showDebug && renderDebug()}
									<Fragment>
										<PatientHeader patient={patient} />

										<h3>Rx Details</h3>
										<Table
											basic="very"
											compact="very"
											verticalAlign="top"
											padded="false"
											collapsing
										>
											<Table.Body>
												<Table.Row key={1}>
													<Table.Cell width="4">Prescriber:</Table.Cell>
													<Table.Cell>{`${rx.PrescriberPractice.Prescriber.prescriberName} ${
														rx.PrescriberPractice.Prescriber.prescriberSurname
													}`}</Table.Cell>
												</Table.Row>
												<Table.Row key={2}>
													<Table.Cell>Practice:</Table.Cell>
													<Table.Cell>{`${
														rx.PrescriberPractice.Practice.practiceName
													}`}</Table.Cell>
												</Table.Row>
												<Table.Row key={3}>
													<Table.Cell>Authority Number:</Table.Cell>
													<Table.Cell>{rx.authorityNo}</Table.Cell>
												</Table.Row>
												<Table.Row key={4}>
													<Table.Cell>Script Date:</Table.Cell>
													<Table.Cell>{rxDate}</Table.Cell>
												</Table.Row>
												<Table.Row key={5}>
													<Table.Cell>Drug:</Table.Cell>
													<Table.Cell>{rx.ItemInfo.itemName}</Table.Cell>
												</Table.Row>
												<Table.Row key={6}>
													<Table.Cell>Treatment:</Table.Cell>
													<Table.Cell>{rx.treatmentType}</Table.Cell>
												</Table.Row>
												<Table.Row key={7}>
													<Table.Cell>Indication:</Table.Cell>
													<Table.Cell>{rx.ItemInfo?.indication}</Table.Cell>
												</Table.Row>
												<Table.Row key={8}>
													<Table.Cell>Item Code:</Table.Cell>
													<Table.Cell>{rx.ItemInfo?.itemCode}</Table.Cell>
												</Table.Row>
												<Table.Row key={9}>
													<Table.Cell>Restriction Code:</Table.Cell>
													<Table.Cell>{rx.ItemInfo?.restrictionCode}</Table.Cell>
												</Table.Row>
												<Table.Row key={10}>
													<Table.Cell verticalAlign="top">Restriction Text:</Table.Cell>
													<Table.Cell>{rx.ItemInfo?.restrictionText}</Table.Cell>
												</Table.Row>
												<Table.Row key={11}>
													<Table.Cell>Eye:</Table.Cell>
													<Table.Cell>{rx.eye}</Table.Cell>
												</Table.Row>
												<Table.Row key={12}>
													<Table.Cell>Number of Repeats:</Table.Cell>
													<Table.Cell>{rx.noOfRepeats}</Table.Cell>
												</Table.Row>
												<Table.Row key={13}>
													<Table.Cell>Quantity:</Table.Cell>
													<Table.Cell>{rx.quantity}</Table.Cell>
												</Table.Row>
												<Table.Row key={14}>
													<Table.Cell>Dose:</Table.Cell>
													<Table.Cell>{rx.dose}</Table.Cell>
												</Table.Row>
											</Table.Body>
										</Table>

										<Form.Field width={16} style={{ marginBottom: '50px' }}>
											<div>Attachment(s)</div>
											<div>
												{!stored.length && !filesToUpload.length && (
													<div className={styles.uploadInfoCont} style={{ height: '28px' }} />
												)}
												{stored.map((f, idx) => (
													<div
														key={idx}
														className={styles.uploadInfoCont}
														style={{ marginLeft: '20px' }}
													>
														<span
															style={{ cursor: 'pointer' }}
															onClick={() => {
																window.open(`/api/containers/${f.fileName}`);
															}}
														>
															{f.label}
														</span>

														<div>
															<Button
																icon
																size="mini"
																onClick={() => {
																	window.open(`/api/containers/${f.fileName}`);
																}}
															>
																<Icon name="file pdf outline" />
															</Button>
														</div>
													</div>
												))}

												{filesToUpload.length >= 1 &&
													filesToUpload.map((f, idx) => (
														<div
															key={idx}
															className={styles.uploadInfoCont}
															style={{ marginLeft: '20px', backgroundColor: '#e0e1e2' }}
														>
															<span className={styles.uploadLabel}>
																{'File to upload: '}
																{`${f.name}`}
															</span>
															<Button
																icon
																size="mini"
																onClick={() => {
																	self.filesToUpload.splice(idx, 1);
																}}
															>
																<Icon name="delete" />
															</Button>
														</div>
													))}
											</div>
										</Form.Field>

										<Form.Group
											style={{ marginBottom: '40px', marginTop: '10px' }}
											className={styles.formGroup}
											formNoValidate={true}
										>
											<Form.Input
												readOnly={isReadOnly}
												required={!isReadOnly}
												label="HPOS Upload Reference"
												value={rx.hposUploadReference || ''}
												onChange={e => {
													let val = e.target.value;
													this.rx.hposUploadReference = val;
												}}
												width={8}
											/>
											{isReadOnly ? (
												<Form.Field width={8}>
													<label>Uploaded to HPOS</label>
													<Form.Input
														value={rx.uploadedtoHPOS?.toDate()?.format() || ''}
														readOnly
													/>
												</Form.Field>
											) : (
												<Form.Field width={8} required>
													<label>Uploaded to HPOS</label>
													<DayPickerInput
														asFormField={false}
														value={rx.uploadedtoHPOS?.toDate() || ''}
														onDayChange={day => {
															this.rx.uploadedtoHPOS = day?.toISOString();
														}}
														dayPickerProps={{
															disabledDays: { after: new Date() }
														}}
													/>
												</Form.Field>
											)}

											{isReadOnly ? (
												<Form.Input
													readOnly
													label="Status"
													value={rx.status?.toUpperCase()}
													width={8}
												/>
											) : (
												<Form.Field width={8} required>
													<label>Status</label>
													<Dropdown
														width={8}
														options={statuses}
														value={this.statusValue}
														onChange={(e, data) => {
															this.statusValue = data.value;
														}}
														selection
													/>
												</Form.Field>
											)}

											{this.statusValue && (
												<Form.Field width={8} required>
													<label>{finalisationLabelMap[this.statusValue]} Date</label>
													<DayPickerInput
														asFormField={false}
														value={rx.finalisationDate?.toDate() || ''}
														onDayChange={day => {
															this.rx.finalisationDate = day?.toISOString();
														}}
														dayPickerProps={{
															disabledDays: { after: new Date() }
														}}
													/>
												</Form.Field>
											)}

											{!this.statusValue && (
												<div className="eight wide field" style={{ display: 'flex' }} />
											)}

											{statusValue === 'approved' && (
												<Fragment>
													<Form.Field width={8} required>
														<label>Approval Number</label>
														<Form.Input
															value={rx.approvalNumber || ''}
															onChange={e => (rx.approvalNumber = e.target.value)}
														/>
													</Form.Field>
												</Fragment>
											)}
											{this.statusValue === 'rejected' && (
												<Form.Field
													required
													control={TextareaAutosize}
													label="Rejection Reason"
													value={rx.reasonText || ''}
													onChange={e => {
														rx.reasonText = e.target.value;
													}}
													width={16}
													rows={1}
												/>
											)}
											{['rejected', 'deferred'].includes(statusValue) && (
												<Form.Field
													control={TextArea}
													label="Notes"
													value={rx.notes || ''}
													onChange={e => {
														rx.notes = e.target.value;
													}}
													width={16}
													style={{
														backgroundColor: 'initial'
													}}
												/>
											)}
											{this.statusValue === 'rejected' && (
												<div className={styles.toggleMargin}>
													<Checkbox
														label="Actioned"
														checked={rx.actioned || false}
														onChange={() => (rx.actioned = !rx.actioned)}
														toggle
													/>
												</div>
											)}
										</Form.Group>

										<div className={styles.buttonsRowSpaced}>
											<div className={styles.buttonCont}>{showButtons && this.renderDelete()}</div>
											<div>
												{showButtons && this.renderButtons()}
												<Button onClick={() => onClose()}>Close</Button>
											</div>
										</div>
									</Fragment>
								</Form>
							</Modal.Content>
						</Fragment>
					</Modal>
					<Modal
						size="mini"
						open={this.showDeleteConfirmDlg}
						onClose={() => (this.showDeleteConfirmDlg = false)}
						className={styles.modalConfirmation}
						closeIcon={true}
					>
						<Modal.Content>
							<p>Do you wish to permanently delete the Rx record?</p>
						</Modal.Content>
						<Modal.Actions className={styles.buttonsRowSpaced}>
							<Button
								onClick={() => {
									this.showDeleteConfirmDlg = false;
								}}
							>
								Cancel
							</Button>
							<Button
								positive
								icon="checkmark"
								labelPosition="right"
								content="OK"
								onClick={() => {
									this.showDeleteConfirmDlg = false;
									this.deleteRx();
								}}
							/>
						</Modal.Actions>
					</Modal>
				</Fragment>
			)
		);
	}
}



// WEBPACK FOOTER //
// ./src/components/Script/ScriptUploaded.js